
import { Component, Inject, Mixins, Prop } from 'vue-property-decorator'
import { Attribute, AttributeOption } from '@/models/Attribute'
import { attributesIncludedInProfileCalculation, Vessel, VesselAttribute } from '@/models/Vessel'
import Testable from '@/mixins/Testable'
import AttributeType from '@/enums/AttributeType'
import { VDialog } from 'vuetify/lib'
import moment from 'moment'
import { namespace } from 'vuex-class'

const Auth = namespace('auth')

@Component({
	computed: {
		AttributeType() {
			return AttributeType
		}
	}
})
export default class AttributeItem extends Mixins(Testable) {
	@Inject('$validator') $validator

	@Prop({ required: true })
	attribute!: Attribute

	@Prop({ required: true })
	vessel!: Vessel

	@Auth.Getter('firstDayOfWeek')
	firstDayOfWeek!: string

	@Auth.Getter('ownVessel')
	ownVessel!: Vessel | null

	get isOwnVessel() {
		return this.ownVessel?.getApiId() === this.vessel.getApiId()
	}

	get shouldFillForProfileCompleteness() {
		return (
			this.isOwnVessel &&
			attributesIncludedInProfileCalculation.includes(this.attribute.slug) &&
			!this.attributeValue
		)
	}

	existingVesselAttribute: VesselAttribute | null = null

	dropDownItems: Array<AttributeOption> = []
	attributeValue: string = ''
	datePickerOpen: boolean = false

	isLoading: boolean = true

	async created() {
		if (this.attribute.getAttributeOptions()) {
			this.dropDownItems = this.attribute.getAttributeOptions()
		}

		this.existingVesselAttribute =
			this.vessel.getVesselAttributes().find((vesselAttribute) => {
				return vesselAttribute.slug === this.attribute.slug
			}) ?? null
		if (this.existingVesselAttribute) {
			this.attributeValue = (this.existingVesselAttribute.value ?? '') as string
		}

		if (this.attribute.attributeType === AttributeType.DATETIME) {
			this.attributeValue = moment(this.attributeValue, 'YYYY-MM-DD', true).isValid() ? this.attributeValue : ''
		}

		this.isLoading = false
	}

	get validationRule() {
		let rule = ''

		if (this.attribute.attributeType === AttributeType.LONGTEXT) {
			rule = 'max:1000'
		}

		switch (this.attribute.slug) {
			case 'mmsi':
				rule = 'between:200000000,800000000'
				break
			case 'length_in_meters_hold_one':
				rule = 'positive:2|between:0,999.99'
				break
			case 'length_in_meters_hold_two':
				rule = 'positive:2|between:0,999.99'
				break
			case 'capacity_in_teu':
				rule = 'positive:2|between:0,9999.99'
				break
			case 'draught':
				rule = 'positive:2|between:0,99.99'
				break
			case 'draught_when_empty':
				rule = 'positive:2|between:0,99.99'
				break
			case 'width':
				rule = 'positive:2|between:0,99.99'
				break
			case 'width_in_meters_hold_one':
				rule = 'positive:2|between:0,99.99'
				break
			case 'width_in_meters_hold_two':
				rule = 'positive:2|between:0,99.99'
				break
			case 'hold_quantity':
				rule = 'positive:0|between:1,1000000'
				break // example positive integer
			case 'hold_height':
				rule = 'positive:2|between:0,99.99'
				break // example positive float, less then 100, max two digits
			case 'push_barge_eni':
				rule = 'eni_number'
				break // example custom,(positive) integer but it can start with a 0. It should be 8 digits
			default:
				break
		}

		return rule
	}

	onDateDialogSave() {
		const dateDialog = this.$refs.dateDialog as VDialog
		dateDialog?.save(this.attributeValue)
		this.setAttributeForVessel()
	}

	async setAttributeForVessel() {
		const valid = await this.$validator.validateAll()
		if (!valid) {
			return
		}

		this.vessel.setVesselAttribute(this.attribute.slug, this.attributeValue)
	}
}
